/**
 * Whether the preview panel stands beside the table — remembered per trainer.
 *
 * Owner decision D4, 17 September 2026. A preference about this trainer's way of working, not a
 * fact about the catalogue, so it belongs in `localStorage` and never in the URL: a link to
 * `/cwiczenia` sent to a colleague opens the way THEY like to work, and the Back button does not
 * step through somebody's layout choices.
 *
 * ── Every unreadable answer is SHOWN ────────────────────────────────────────────────────────
 * CLAUDE.md's rule on browser-persisted data: a record written by an older build is read
 * defensively, never assumed to carry today's shape, never allowed to throw. Four things can go
 * wrong here and all four land on the panel being visible — a value this build does not recognise,
 * a store whose own methods throw, a browser with no store at all, and the absence of the key,
 * which is the ordinary first visit.
 *
 * Shown rather than the last thing that parsed, because the panel is what makes this a master-detail
 * screen: a trainer who lands on a bare table has lost the half of the screen that describes what
 * they are pointing at, and a trainer who lands on the panel has lost a preference they can restate
 * with one press. Same argument, same shape, as `plan-builder`'s `dockDefault`.
 *
 * ── The preference is HALF the answer, and `panelVisible` is the whole one ──────────────────
 * Below 1424px there is no room for the column and it is not drawn whatever the trainer prefers.
 * That used to be a `@media` block in the screen's stylesheet, invisible to the three things that
 * have to agree with it: the toolbar's `aria-pressed`, the toolbar's title, and the muscle cap the
 * table draws. At 1300x800 a movement with three groups therefore drew two pills and a „+1" with
 * no panel anywhere on the screen, and the toggle reported „pressed" for a column nothing could
 * see — and pressing it WROTE `hidden` for the wide window the trainer would next open.
 *
 * So the width is a reading here rather than a rule in CSS, and the whole screen asks one question.
 */

import type { PreferenceStore } from '~/shared/lib/preferenceStore';

/** Whether the 396px column is drawn. Two states and no third — it is a toggle. */
export type PanelPreference = 'shown' | 'hidden';

/** `movebuddy.` prefixed, as every preference this app stores is. */
const KEY = 'movebuddy.exercisePanel';

/** The answer to anything unreadable. See the header. */
const FALLBACK: PanelPreference = 'shown';

export function readPanelPreference(store: PreferenceStore | null): PanelPreference {
  if (store === null) return FALLBACK;
  try {
    const stored = store.getItem(KEY);
    return stored === 'shown' || stored === 'hidden' ? stored : FALLBACK;
  } catch {
    // Reading storage can THROW rather than answer null, and a layout preference is never worth
    // taking the screen down for.
    return FALLBACK;
  }
}

/**
 * The width at which the two panes still fit, as a media query.
 *
 * **The ROSTER's number, deliberately, and not recomputed.** The arithmetic says this screen could
 * collapse at 1364px (a 816px grid floor plus the rail, the measure's inset, the pane gap, the
 * 396px panel, the card's border and the reserved scrollbar gutter); the clients roster's own floor
 * is 876 and it collapses at 1424. A trainer resizing a window should not watch one master-detail
 * screen drop its panel while the other beside it keeps one, and the 60px band between the two is
 * what that costs.
 */
export const PANEL_FITS_QUERY = '(width >= 1424px)';

/**
 * Whether the column is actually drawn: the trainer's answer AND the room to honour it.
 *
 * The one reading the toggle's state, the toggle's title, the table's muscle cap and the column
 * itself all take, so none of them can describe a screen the others are not drawing.
 */
export function panelVisible(preference: PanelPreference, fits: boolean): boolean {
  return fits && preference === 'shown';
}

export function writePanelPreference(
  store: PreferenceStore | null,
  preference: PanelPreference,
): void {
  if (store === null) return;
  try {
    store.setItem(KEY, preference);
  } catch {
    // A full or refused store loses the preference and nothing else. The panel the trainer just
    // toggled is already on screen either way, so the act they took still happened.
  }
}
